import { useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { hostFor } from '#lib/host/host';
import { hostMode, type HostMode } from '#lib/host/mode';
import { HostTheme } from './host-theme.js';
import { EmbeddedGate } from './embedded-gate.js';
import { StandaloneGate } from './standalone-gate.js';

/**
 * TASKS-APP-FULL-UI-1 U2b — what stands between the page and the app, by mode: an app FRAMED by Vitto enters through the door
 * (`EmbeddedGate`); every other mode is the product as it was (its own login). The decision is the document's, made once at load.
 */
export const HostGate = ({
  mode = hostMode,
  children,
}: {
  mode?: HostMode;
  children: ReactNode;
}) => {
  const client = useQueryClient();
  if (mode.kind === 'own') return children;
  const host = hostFor(mode);
  // a top-level tab of a deploy that knows its Vitto: only the RETURN from «Continue with Vitto» needs the door; the rest is the product's own login
  if (mode.kind === 'standalone')
    return host === null ? children : <StandaloneGate host={host}>{children}</StandaloneGate>;
  // another account behind the same document: the CACHE of the previous person goes with the tree (the query client lives above the gate)
  return host === null ? (
    children
  ) : (
    <HostTheme host={host}>
      <EmbeddedGate
        host={host}
        vittoOrigin={mode.config.vittoOrigin}
        onAccountChanged={() => client.clear()}
      >
        {children}
      </EmbeddedGate>
    </HostTheme>
  );
};
